'use client';

import React, { useState } from 'react';
import { Search, Bell, RefreshCw, ChevronDown, Menu } from 'lucide-react';

interface TopbarProps {
  sidebarCollapsed: boolean;
  onMobileMenuToggle: () => void;
  title?: string;
  subtitle?: string;
}

const notifications = [
  { id: 'notif-001', type: 'danger', message: 'Lava A3 Mini — 0 units at Malur. Stockout imminent.', time: '8 min ago' },
  { id: 'notif-002', type: 'warning', message: 'Moto E13 ROAS dropped to 1.2 at Bhiwandi campaign.', time: '34 min ago' },
  { id: 'notif-003', type: 'warning', message: 'Uluberia inventory ageing >60 days for 2 SKUs.', time: '1 hr ago' },
  { id: 'notif-004', type: 'info', message: 'Settlement ₹3.42L received for 28–31 Jul cycle.', time: '3 hr ago' },
  { id: 'notif-005', type: 'success', message: 'Daily management report sent to management@radsan.in', time: '5 hr ago' },
];

export default function Topbar({ sidebarCollapsed, onMobileMenuToggle, title, subtitle }: TopbarProps) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handleSync = () => {
    setSyncing(true);
    setTimeout(() => setSyncing(false), 2200);
  };

  const typeColors: Record<string, string> = {
    danger: 'bg-negative-bg text-negative border-l-2 border-negative',
    warning: 'bg-warning-bg text-warning border-l-2 border-warning',
    info: 'bg-info-bg text-info border-l-2 border-info',
    success: 'bg-positive-bg text-positive border-l-2 border-positive',
  };

  return (
    <header
      className="fixed top-0 right-0 h-16 bg-card border-b border-border flex items-center px-4 gap-3 z-20 transition-all duration-300"
      style={{ left: sidebarCollapsed ? '64px' : '240px' }}
    >
      <button
        className="lg:hidden p-2 rounded-lg hover:bg-muted text-muted-foreground"
        onClick={onMobileMenuToggle}
        aria-label="Open menu"
      >
        <Menu size={20} />
      </button>

      <div className="flex flex-col min-w-0 flex-1">
        {title && <h1 className="text-base font-700 text-foreground leading-tight truncate">{title}</h1>}
        {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
      </div>

      {/* Search */}
      <div className="hidden md:flex items-center gap-2 bg-muted rounded-lg px-3 py-1.5 w-64">
        <Search size={14} className="text-muted-foreground flex-shrink-0" />
        <input
          type="text"
          placeholder="Search SKU, FSN, order..."
          className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none w-full"
        />
        <span className="text-2xs text-muted-foreground bg-card border border-border rounded px-1 py-0.5 hidden lg:block">⌘K</span>
      </div>

      {/* Sync */}
      <button
        onClick={handleSync}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted hover:bg-secondary text-muted-foreground hover:text-foreground text-xs font-500 transition-colors duration-150 btn-press"
        title="Sync data from Flipkart"
      >
        <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
        <span className="hidden sm:block">{syncing ? 'Syncing...' : 'Sync'}</span>
      </button>

      {/* Notifications */}
      <div className="relative">
        <button
          onClick={() => setShowNotifications(!showNotifications)}
          className="relative p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors duration-150 btn-press"
          aria-label="Notifications"
        >
          <Bell size={18} />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-negative rounded-full alert-pulse" />
        </button>

        {showNotifications && (
          <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-xl shadow-dropdown z-50 fade-in">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <span className="text-sm font-600 text-foreground">Alerts & Notifications</span>
              <span className="text-2xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">5 new</span>
            </div>
            <div className="max-h-80 overflow-y-auto scrollbar-thin">
              {notifications.map((n) => (
                <div key={n.id} className={`px-4 py-3 border-b border-border/50 ${typeColors[n.type]} m-2 rounded-lg`}>
                  <p className="text-xs font-500 leading-snug">{n.message}</p>
                  <p className="text-2xs mt-1 opacity-70">{n.time}</p>
                </div>
              ))}
            </div>
            <div className="px-4 py-2 text-center">
              <button className="text-xs text-primary font-500 hover:underline">View all alerts</button>
            </div>
          </div>
        )}
      </div>

      {/* User */}
      <div className="flex items-center gap-2 pl-2 border-l border-border">
        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-xs font-700">
          NB
        </div>
        <div className="hidden md:flex flex-col">
          <span className="text-xs font-600 text-foreground leading-tight">Naman Bansal</span>
          <span className="text-2xs text-muted-foreground">Super Admin</span>
        </div>
        <ChevronDown size={12} className="text-muted-foreground hidden md:block" />
      </div>
    </header>
  );
}